import { useMutation } from "@tanstack/react-query";
import type { MutationOptions } from "@tanstack/react-query";
import { customFetch } from "./custom-fetch";

// ---------------------------------------------------------------------------
// POST /api/events/:id/repost-puller-alerts
// Re-resolve pullers for an event's already-posted "unknown puller" Slack
// intent-reply alerts and edit those messages in place. Hand-written (not
// codegen) so the endpoint stays stable regardless of orval regeneration.
// ---------------------------------------------------------------------------

export type RepostPullerAlertsVars = {
  id: number;
  /** Re-link vendor accounts via purchase enrichment first. Defaults to true. */
  relink?: boolean;
};

export type RepostPullerAlertsResult = {
  eventId: number;
  relink: {
    total: number;
    alreadyLinked: number;
    matched: number;
    noMatch: number;
    purchasesReturned: number;
  } | null;
  messagesTotal: number;
  messagesUpdated: number;
  messagesSkippedUnknown: number;
  messagesFailed: number;
  stillUnknownInventoryIds: number[];
  details: Array<{
    messageTs: string;
    inventoryIds: number[];
    status: "updated" | "skipped_unknown" | "failed";
    error?: string;
  }>;
};

export const repostPullerAlerts = ({ id, relink }: RepostPullerAlertsVars) =>
  customFetch<RepostPullerAlertsResult>(
    `/api/events/${id}/repost-puller-alerts`,
    {
      method: "POST",
      body: JSON.stringify({ relink: relink ?? true }),
    },
  );

export const useRepostPullerAlerts = <TError = unknown, TContext = unknown>(options?: {
  mutation?: MutationOptions<
    RepostPullerAlertsResult,
    TError,
    RepostPullerAlertsVars,
    TContext
  >;
}) =>
  useMutation<
    RepostPullerAlertsResult,
    TError,
    RepostPullerAlertsVars,
    TContext
  >({
    mutationFn: repostPullerAlerts,
    ...options?.mutation,
  });
